import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import RichTextEditor from "./RichTextEditor";
import { useToast } from "../../hooks/use-toast";
import { FileText, Save, RotateCcw, Eye, Loader2, Printer } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentTextsEditor = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [texts, setTexts] = useState([]);
  const [variables, setVariables] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);

  const openDemoPdf = async () => {
    try {
      setDemoLoading(true);
      const res = await axios.post(`${API_URL}/api/admin/document-texts/preview-pdf`,
        { texts: drafts }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: "Eroare", description: "Nu am putut genera PDF-ul demo.", variant: "destructive" });
    } finally {
      setDemoLoading(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/document-texts`);
      setTexts(res.data.texts || []);
      setVariables(res.data.variables || []);
      const d = {};
      (res.data.texts || []).forEach((t) => { d[t.key] = t.value; });
      setDrafts(d);
    } catch (e) {
      toast({ title: "Eroare", description: "Nu am putut încărca textele.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (key) => {
    try {
      setSaving(key);
      await axios.put(`${API_URL}/api/admin/document-texts/${key}`, { value: drafts[key] });
      toast({ title: "✅ Salvat", description: "Textul a fost actualizat. Va apărea în documentele generate de acum înainte." });
      await load();
    } catch (e) {
      toast({ title: "Eroare", description: e?.response?.data?.detail || "Salvarea a eșuat.", variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  const reset = async (key) => {
    if (!window.confirm("Sigur revii la textul standard implicit? Modificările tale vor fi șterse.")) return;
    try {
      setSaving(key);
      await axios.delete(`${API_URL}/api/admin/document-texts/${key}`);
      toast({ title: "↩ Revenit la implicit", description: "Textul standard a fost restaurat." });
      await load();
    } catch (e) {
      toast({ title: "Eroare", description: "Resetarea a eșuat.", variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  const renderPreview = (html) => {
    let out = html;
    (variables || []).forEach((v) => {
      out = out.split(`{{${v.key}}}`).join(`<mark style="background:#fde68a;padding:0 2px;">[${v.key}]</mark>`);
    });
    return out;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500" data-testid="doctexts-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Se încarcă textele...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-texts-editor">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-blue-700 mt-0.5" />
          <div className="text-sm text-blue-900 flex-1">
            <div className="flex items-start justify-between gap-3">
              <p className="font-semibold mb-1">Editor texte standard documente</p>
              <Button size="sm" onClick={openDemoPdf} disabled={demoLoading}
                className="shrink-0 bg-blue-700 hover:bg-blue-800" data-testid="doctext-demo-pdf-btn">
                {demoLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />}
                Vezi PDF demo (printabil)
              </Button>
            </div>
            <p>Modifică textele legale standard din „Informare Consumator" și „Declarație Consumator". Dacă nu modifici nimic, rămâne textul implicit. Poți folosi variabilele de mai jos — se completează automat la generarea PDF-ului.</p>
            <p className="text-xs text-blue-700 mt-1">PDF-ul demo folosește textele curente (inclusiv modificările nesalvate) cu spații libere în locul datelor — îl poți printa și completa cu pixul.</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {variables.map((v) => (
                <Badge key={v.key} variant="secondary" className="font-mono text-xs" title={v.desc}>{`{{${v.key}}}`}</Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {texts.map((t) => (
        <Card key={t.key} data-testid={`doctext-card-${t.key}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                {t.label}
                {t.is_custom
                  ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Personalizat</Badge>
                  : <Badge variant="secondary">Text standard</Badge>}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPreviewKey(previewKey === t.key ? null : t.key)} data-testid={`doctext-preview-${t.key}`}>
                  <Eye className="h-4 w-4 mr-1" /> {previewKey === t.key ? "Ascunde" : "Previzualizare"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => reset(t.key)} disabled={!t.is_custom || saving === t.key} data-testid={`doctext-reset-${t.key}`}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Text implicit
                </Button>
                <Button size="sm" onClick={() => save(t.key)} disabled={saving === t.key || drafts[t.key] === t.value} data-testid={`doctext-save-${t.key}`}>
                  {saving === t.key ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salvează
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <RichTextEditor
              value={drafts[t.key] ?? ""}
              onChange={(html) => setDrafts({ ...drafts, [t.key]: html })}
              variables={variables}
              testid={`doctext-editor-${t.key}`}
            />
            {previewKey === t.key && (
              <div className="mt-4 border rounded-lg p-4 bg-white" data-testid={`doctext-previewbox-${t.key}`}>
                <p className="text-xs text-slate-500 mb-2">Previzualizare (variabilele sunt marcate; se completează la generare):</p>
                <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: renderPreview(drafts[t.key] || "") }} />
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DocumentTextsEditor;
